import { CaseStatus, MortgageReason, SearchResultCase, SearchResultItem } from '@acre/graphql'

/**
 * Removes InvalidStatus cases and split the active and inactive cases in two different arrays
 */
export const reduceActiveInactiveCases = (cases: SearchResultCase[]): [SearchResultCase[], SearchResultCase[]] => {
  if (!cases) return [[], []]

  return cases?.reduce<[SearchResultCase[], SearchResultCase[]]>(
    ([activeCases, inactiveCases], caseObj) => {
      switch (caseObj?.case_status) {
        case CaseStatus.Complete:
          activeCases.push(caseObj as SearchResultCase)
          break
        case CaseStatus.NotProceeding:
          inactiveCases.push(caseObj as SearchResultCase)
          break
        case CaseStatus.InvalidStatus:
          break
        default:
          activeCases.push(caseObj as SearchResultCase)
          break
      }
      return [activeCases, inactiveCases]
    },
    [[], []],
  )
}

export const getProductTypeIntl = (reason: MortgageReason): string => {
  return `productTypes.${STATUS_INTL[reason as keyof typeof STATUS_INTL]}`
}

const STATUS_INTL = {
  [MortgageReason.ReasonFtb]: 'firstTimeBuy',
  [MortgageReason.ReasonHouseMove]: 'houseMove',
  [MortgageReason.ReasonRemortgage]: 'remortgage',
  [MortgageReason.ReasonBtl]: 'buyToLet',
  [MortgageReason.ReasonBtlRemortgage]: 'buyToLetRemortgage',
  [MortgageReason.ReasonEquityRelease]: 'equityRelease',
  [MortgageReason.ReasonFurtherAdvance]: 'furtherAdvance',
  [MortgageReason.ReasonBusinessProtection]: 'businessProtection',
  [MortgageReason.ReasonProtection]: 'protection',
  [MortgageReason.InvalidMortgageReason]: 'noReasonProvided',
  [MortgageReason.ReasonCommercial]: 'commercial',
  [MortgageReason.ReasonBridging]: 'bridging',
  [MortgageReason.ReasonGeneralInsurance]: 'homeInsurance',
}

/**
 * Generates a URL for navigating to either a client's overview page or a specific case's overview page following this logic:
 *  - URL to a specific case if only one case is associated with the client
 *  - URL to the client overview page otherwise
 *  - empty string if the option has no ID and no cases
 */
export const getUrlForOption = (option: SearchResultItem) => {
  if (!option) return ''

  const [activeCases, inactiveCases] = reduceActiveInactiveCases(option?.Cases ?? [])
  const cases = [...activeCases, ...inactiveCases]

  if (!option.id && cases.length === 0) return ''

  // if there's exactly one case, navigate directly to that case
  if (cases.length === 1) {
    const caseId = cases[0].id.replace(/.*_/, '')
    return `/cases/${caseId}/overview`
  }

  // navigate to the client overview page by default
  return `/clients/${option.id}/overview`
}
